/**
 * MapManager Singleton Service
 * Centralized service for all map operations using Mapbox native APIs
 * Prevents duplicate operations and manages layer/marker/polygon lifecycle
 */
import type { AnyLayer, AnySourceData, FillLayer, GeoJSONSource, LineLayer, Map as MapboxMap, Marker } from 'mapbox-gl';
import { LAYER_PREFIXES, SOURCE_PREFIXES } from '@/constants/mapConstants';

interface LayerRegistry {
  layerId: string;
  sourceId?: string;
  type: 'layer' | 'marker' | 'polygon';
}

class MapManager {
  private static instance: MapManager;
  private map: MapboxMap | null = null;
  private layers: Map<string, LayerRegistry> = new Map();
  private markers: Map<string, Marker> = new Map();
  private initialized: boolean = false;

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): MapManager {
    if (!MapManager.instance) {
      MapManager.instance = new MapManager();
    }
    return MapManager.instance;
  }

  /**
   * Initialize the MapManager with a map instance
   * Should be called once when map is ready
   */
  initialize(map: MapboxMap): void {
    if (this.initialized && this.map === map) {
      return; // Already initialized with this map
    }

    this.map = map;
    this.initialized = true;
    this.layers.clear();
    this.markers.clear();
  }

  /**
   * Get the current map instance
   */
  getMap(): MapboxMap | null {
    return this.map;
  }

  /**
   * Check if map is ready and style is loaded
   */
  private ensureMapReady(): boolean {
    if (!this.map || !this.map.isStyleLoaded()) {
      console.warn('MapManager: Map not ready or style not loaded');
      return false;
    }
    return true;
  }

  /**
   * Add a layer to the map
   * @param layerId - Unique layer identifier
   * @param layerConfig - Mapbox layer configuration
   * @param sourceConfig - Mapbox source configuration (if needed)
   * @param beforeLayerId - Optional layer ID to insert before
   */
  addLayer(
    layerId: string,
    layerConfig: AnyLayer,
    sourceConfig?: AnySourceData,
    beforeLayerId?: string | null,
  ): boolean {
    if (!this.ensureMapReady() || !this.map) {
      return false;
    }

    const map = this.map;

    // Check if layer already exists
    if (this.layers.has(layerId) || map.getLayer(layerId)) {
      console.warn(`MapManager: Layer ${layerId} already exists`);
      return false;
    }

    try {
      // Add source if provided
      if (sourceConfig) {
        const sourceId = (layerConfig as { source?: string }).source || layerId;
        if (!map.getSource(sourceId)) {
          map.addSource(sourceId, sourceConfig);
        }
      }

      // Find insertion point if beforeLayerId is not provided
      let insertBefore = beforeLayerId;
      if (!insertBefore) {
        const style = map.getStyle();
        const layers = style?.layers ?? [];
        for (let i = layers.length - 1; i >= 0; i--) {
          const layer = layers[i];
          if (layer.type === 'symbol' || layer.id.includes('label')) {
            insertBefore = layer.id;
            break;
          }
        }
      }

      // Add layer
      map.addLayer(layerConfig, insertBefore || undefined);

      // Register layer
      this.layers.set(layerId, {
        layerId,
        sourceId: layerConfig.source,
        type: 'layer',
      });

      return true;
    } catch (error) {
      console.error(`MapManager: Error adding layer ${layerId}:`, error);
      return false;
    }
  }

  /**
   * Remove a layer from the map
   * @param layerId - Layer identifier to remove
   * @param removeSource - Whether to also remove the associated source
   */
  removeLayer(layerId: string, removeSource: boolean = false): boolean {
    if (!this.ensureMapReady() || !this.map) {
      return false;
    }

    const map = this.map;

    try {
      const layer = this.layers.get(layerId);

      // Remove layer if it exists
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }

      // Remove source if requested and exists
      if (removeSource && layer?.sourceId) {
        const source = map.getSource(layer.sourceId);
        if (source) {
          map.removeSource(layer.sourceId);
        }
      }

      // Unregister layer
      this.layers.delete(layerId);

      return true;
    } catch (error) {
      console.error(`MapManager: Error removing layer ${layerId}:`, error);
      return false;
    }
  }

  /**
   * Update layer visibility
   * @param layerId - Layer identifier
   * @param visible - Whether layer should be visible
   */
  setLayerVisibility(layerId: string, visible: boolean): boolean {
    if (!this.ensureMapReady() || !this.map) {
      return false;
    }

    const map = this.map;

    try {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
        return true;
      }
      return false;
    } catch (error) {
      console.error(`MapManager: Error setting layer visibility ${layerId}:`, error);
      return false;
    }
  }

  /**
   * Update source data
   * @param sourceId - Source identifier
   * @param data - New GeoJSON data
   */
  updateSourceData(sourceId: string, data: GeoJSON.FeatureCollection | GeoJSON.Feature): boolean {
    if (!this.ensureMapReady() || !this.map) {
      return false;
    }

    const map = this.map;

    try {
      const source = map.getSource(sourceId) as GeoJSONSource | undefined;
      if (source) {
        source.setData(data);
        return true;
      }
      return false;
    } catch (error) {
      console.error(`MapManager: Error updating source data ${sourceId}:`, error);
      return false;
    }
  }

  /**
   * Add a marker to the map
   * @param markerId - Unique marker identifier
   * @param marker - Mapbox Marker instance
   */
  addMarker(markerId: string, marker: Marker): boolean {
    if (!this.map) {
      console.warn('MapManager: Map not initialized');
      return false;
    }

    // Remove existing marker with same ID
    if (this.markers.has(markerId)) {
      this.removeMarker(markerId);
    }

    try {
      marker.addTo(this.map);
      this.markers.set(markerId, marker);
      return true;
    } catch (error) {
      console.error(`MapManager: Error adding marker ${markerId}:`, error);
      return false;
    }
  }

  /**
   * Remove a marker from the map
   * @param markerId - Marker identifier to remove
   */
  removeMarker(markerId: string): boolean {
    const marker = this.markers.get(markerId);
    if (marker) {
      try {
        marker.remove();
        this.markers.delete(markerId);
        return true;
      } catch (error) {
        console.error(`MapManager: Error removing marker ${markerId}:`, error);
        return false;
      }
    }
    return false;
  }

  /**
   * Get a marker by ID
   * @param markerId - Marker identifier
   */
  getMarker(markerId: string): Marker | undefined {
    return this.markers.get(markerId);
  }

  /**
   * Add a polygon to the map
   * @param polygonId - Unique polygon identifier
   * @param geojson - GeoJSON polygon data
   * @param fillStyle - Fill layer style configuration
   * @param outlineStyle - Outline layer style configuration
   */
  addPolygon(
    polygonId: string,
    geojson: GeoJSON.FeatureCollection | GeoJSON.Feature,
    fillPaint?: FillLayer['paint'],
    outlinePaint?: LineLayer['paint'],
  ): boolean {
    if (!this.ensureMapReady() || !this.map) {
      return false;
    }

    const map = this.map;

    // Use polygonId directly if provided, otherwise use the prefix
    const sourceId = polygonId
      ? `${SOURCE_PREFIXES.SELECTED_GEOFENCE}-${polygonId}`
      : SOURCE_PREFIXES.SELECTED_GEOFENCE;
    const fillLayerId = polygonId
      ? `${LAYER_PREFIXES.SELECTED_GEOFENCE_FILL}-${polygonId}`
      : LAYER_PREFIXES.SELECTED_GEOFENCE_FILL;
    const outlineLayerId = polygonId
      ? `${LAYER_PREFIXES.SELECTED_GEOFENCE_OUTLINE}-${polygonId}`
      : LAYER_PREFIXES.SELECTED_GEOFENCE_OUTLINE;

    // Remove existing polygon if it exists
    this.removePolygon(polygonId);

    try {
      // Add source
      map.addSource(sourceId, {
        type: 'geojson',
        data: geojson,
      });

      // Find insertion point
      const style = map.getStyle();
      const layers = style?.layers ?? [];
      let beforeLayerId: string | null = null;
      for (let i = layers.length - 1; i >= 0; i--) {
        const layer = layers[i];
        if (layer.type === 'symbol' || layer.id.includes('label')) {
          beforeLayerId = layer.id;
          break;
        }
      }

      // Add fill layer
      const fillConfig: FillLayer = {
        id: fillLayerId,
        type: 'fill',
        source: sourceId,
        paint: fillPaint || {
          'fill-color': '#3b82f6',
          'fill-opacity': 0.2,
        },
      };
      map.addLayer(fillConfig, beforeLayerId || undefined);

      // Add outline layer
      const outlineConfig: LineLayer = {
        id: outlineLayerId,
        type: 'line',
        source: sourceId,
        paint: outlinePaint || {
          'line-color': '#60a5fa',
          'line-width': 2.5,
        },
      };
      map.addLayer(outlineConfig, beforeLayerId || undefined);

      // Register layers
      this.layers.set(fillLayerId, {
        layerId: fillLayerId,
        sourceId,
        type: 'polygon',
      });
      this.layers.set(outlineLayerId, {
        layerId: outlineLayerId,
        sourceId,
        type: 'polygon',
      });

      return true;
    } catch (error) {
      console.error(`MapManager: Error adding polygon ${polygonId}:`, error);
      return false;
    }
  }

  /**
   * Remove a polygon from the map
   * @param polygonId - Polygon identifier to remove
   */
  removePolygon(polygonId: string): boolean {
    if (!this.ensureMapReady() || !this.map) {
      return false;
    }

    const map = this.map;

    // Use polygonId directly if provided, otherwise use the prefix
    const sourceId = polygonId
      ? `${SOURCE_PREFIXES.SELECTED_GEOFENCE}-${polygonId}`
      : SOURCE_PREFIXES.SELECTED_GEOFENCE;
    const fillLayerId = polygonId
      ? `${LAYER_PREFIXES.SELECTED_GEOFENCE_FILL}-${polygonId}`
      : LAYER_PREFIXES.SELECTED_GEOFENCE_FILL;
    const outlineLayerId = polygonId
      ? `${LAYER_PREFIXES.SELECTED_GEOFENCE_OUTLINE}-${polygonId}`
      : LAYER_PREFIXES.SELECTED_GEOFENCE_OUTLINE;

    try {
      // Remove layers
      if (map.getLayer(fillLayerId)) {
        map.removeLayer(fillLayerId);
      }
      if (map.getLayer(outlineLayerId)) {
        map.removeLayer(outlineLayerId);
      }

      // Remove source
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }

      // Unregister layers
      this.layers.delete(fillLayerId);
      this.layers.delete(outlineLayerId);

      return true;
    } catch (error) {
      console.error(`MapManager: Error removing polygon ${polygonId}:`, error);
      return false;
    }
  }

  /**
   * Check if a layer exists
   * @param layerId - Layer identifier
   */
  hasLayer(layerId: string): boolean {
    if (!this.map) return false;
    return this.map.getLayer(layerId) !== undefined;
  }

  /**
   * Check if a source exists
   * @param sourceId - Source identifier
   */
  hasSource(sourceId: string): boolean {
    if (!this.map) return false;
    return this.map.getSource(sourceId) !== undefined;
  }

  /**
   * Get all registered layer IDs
   */
  getRegisteredLayers(): string[] {
    return Array.from(this.layers.keys());
  }

  /**
   * Get all registered marker IDs
   */
  getRegisteredMarkers(): string[] {
    return Array.from(this.markers.keys());
  }

  /**
   * Clear all layers and markers (use with caution)
   */
  clearAll(): void {
    // Remove all markers
    this.markers.forEach((marker, id) => {
      try {
        marker.remove();
      } catch (error) {
        console.error(`MapManager: Error removing marker ${id}:`, error);
      }
    });
    this.markers.clear();

    // Remove all registered layers
    this.layers.forEach((registry, layerId) => {
      try {
        if (this.map?.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
        if (registry.sourceId && this.map?.getSource(registry.sourceId)) {
          this.map.removeSource(registry.sourceId);
        }
      } catch (error) {
        console.error(`MapManager: Error removing layer ${layerId}:`, error);
      }
    });
    this.layers.clear();
  }

  /**
   * Reset the MapManager (for testing or re-initialization)
   */
  reset(): void {
    this.clearAll();
    this.map = null;
    this.initialized = false;
  }
}

// Export singleton instance
export const mapManager = MapManager.getInstance();
